import { StateType } from '../../../../../state/state.types';
import {
  CategoryEnum,
  AggregationsType,
  LegislationMetaType,
} from '../../../../search.types';
import { aggregationSeparator } from '../../../../state/utils/constants';
import { getFilterLastPartAfterArrow } from '../../../../search-utils';

export const buildDateFilter = ({
  from,
  to,
  exact,
}: {
  from?: string;
  to?: string;
  exact?: string;
}) => {
  if (from && !to) return `Depuis ${from}`;
  if (from && to && from !== to) return `De ${from} à ${to}`;
  if (from && to && from === to) return from;
  if (exact) return exact;
};

export const buildAggregationFilters = (
  aggregationsPaths: Array<string>,
  type: CategoryEnum,
  // is only useful if we want to enhance the tree with display like names
  aggregations?: AggregationsType,
) => {
  // there is no selection (aggregationsPaths are really selectedAggregationsPaths)
  if (!aggregationsPaths.length) return [];

  // we sometimes call buildAggregationFilters several times in a row
  // with different types and each time we rebuild that exact same tree
  // we could do better like store it in the store
  // if we keep it here we could at least only build the sub tree that
  // corresponds to "type" instead of the full thing
  const tree = aggregationsPaths.reduce((acc, item) => {
    const paths = item.split(aggregationSeparator);

    let cursor = acc;
    paths.forEach(path => {
      if (!cursor[path]) {
        // we create a child element for the current path segment (key) if not exist yet
        cursor[path] = {};
      }
      cursor = cursor[path];
    });

    return acc;
  }, <{ [s: string]: {} }>{});

  if (!tree[type]) return [];

  const filters: Array<{ label: string; path: Array<string> }> = [];
  const dig = (
    branch: { [s: string]: {} },
    path: Array<{ key: string; brothers: number }>,
  ) => {
    const children = Object.keys(branch);

    if (children.length === 0) {
      const [, ...rest] = path;
      const label = rest
        .map(({ key }, index) => {
          // if we are at first level and the caller asked for it
          // we want to use abbreviation if it exists
          if (index === 0 && aggregations && aggregations[type]) {
            const aggregationItem = aggregations[type].find(
              item => item.key === key,
            );
            return aggregationItem?.abbreviation || key;
          }
          // in some cases deeper labels are of the form A -> B, A is just going to be a repeat
          // of another selected label so let's remove it
          return getFilterLastPartAfterArrow(key);
        })
        .join(' ');
      /* look up for the first (from leaf) common ancestor */
      const index =
        path.length -
        1 -
        path
          .slice()
          .reverse()
          .findIndex(({ brothers }) => brothers > 0);
      const hasAncestor = index < path.length;
      if (hasAncestor) {
        filters.push({
          label,
          path: path.slice(0, index + 1).map(({ key }) => key),
        });
      } else {
        filters.push({
          label,
          path: path.slice(0, 2).map(({ key }) => key),
        });
      }
    } else {
      children.forEach(key => {
        dig(branch[key], path.concat({ key, brothers: children.length - 1 }));
      });
    }
  };

  dig(tree[type], [{ key: type, brothers: 0 }]);

  return filters;
};

export const buildPartiesFilter = ({
  defendant,
  plaintiff,
}: StateType['queryParams']['parties']) => {
  const getLabel = ({ type, name }: { type?: string; name?: string }) => {
    if (name) return name;
    if (type === 'PP') return 'Personne physique';
    if (type === 'PM') return 'Personne morale';
  };
  if (defendant && plaintiff) {
    return `${getLabel(plaintiff)} contre ${getLabel(defendant)}`;
  }
  if (plaintiff && !defendant) {
    return `Demandeur : ${getLabel(plaintiff)}`;
  }
  if (defendant && !plaintiff) {
    return `Défendeur : ${getLabel(defendant)}`;
  }
};

export const buildLegislationFilter = ({
  title,
  startingDateHtml,
  endingDateHtml,
}: LegislationMetaType) => {
  const getDate = () => {
    if (startingDateHtml && endingDateHtml) {
      return `du ${startingDateHtml} au ${endingDateHtml}`;
    }
    if (startingDateHtml && !endingDateHtml) {
      return `depuis le ${startingDateHtml}`;
    }
  };
  return `${title} version en vigueur ${getDate()}`;
};
